import { useState, useEffect } from 'react';
import { PanelHowTo, api } from './_barrel.js';


        // One-page "how findable is this person" summary for the currently
        // selected subject: the AEA location-exposure grade, address exposure
        // (Companies House filings), breach exposure, and Pipl / OSINT
        // Industries identity findings — each read-only, already computed
        // elsewhere, and shown with the same grade/severity language as the
        // panels they come from. A gated source (AEA consent lapsed, or a
        // Pipl/OSINT third-party subject with no lawful-basis note on file)
        // reads "Locked", never a blank "nothing found".
        // Reads GET /api/v1/subjects/{id}/exposure-summary.
        const STATUS_LABEL = {
            findings: 'Findings', found: 'Found', found_not_included: 'Not in report',
            none: 'None', not_run: 'Not run', not_registered: 'Not registered',
            no_data: 'No data', locked: 'Locked',
        };

        function severityHex(sev) {
            const k = String(sev || '').toLowerCase();
            if (k === 'critical') return 'var(--red)';
            if (k === 'high') return 'var(--amber)';
            if (k === 'moderate') return 'var(--gold)';
            return 'var(--text-muted)';
        }

        function ExposureSummaryRow({ title, section }) {
            if (!section) return null;
            const graded = section.status === 'graded' && section.grade;
            const hex = graded ? section.hex
                      : section.status === 'locked' ? 'var(--text-dim)'
                      : section.severity ? severityHex(section.severity)
                      : 'var(--text-muted)';
            return (
                <div className="table-row px-4 py-3" style={{ borderLeft: `3px solid ${hex}` }}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="text-sm" style={{ color: 'var(--text)' }}>{title}</span>
                                {graded && <span className="label-tag" style={{ color: hex }}>{section.grade}</span>}
                                {!graded && section.severity && (
                                    <span className={`label-tag sev-${section.severity}`}>{section.severity}</span>
                                )}
                                {!graded && !section.severity && (
                                    <span className="label-tag" style={{ color: hex }}>
                                        {STATUS_LABEL[section.status] || section.status}
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-muted">{section.headline}</div>
                        </div>
                    </div>
                    {section.action && (
                        <div className="text-xs mt-2" style={{ color: 'var(--text-soft)' }}>▸ {section.action}</div>
                    )}
                </div>
            );
        }

        function ExposureSummaryPanel({ subjectId, token }) {
            const [data, setData] = useState(null);
            const [err, setErr] = useState(null);

            useEffect(() => {
                let live = true;
                setData(null); setErr(null);
                api(`/api/v1/subjects/${subjectId}/exposure-summary`, {}, token)
                    .then(r => { if (live) setData(r); })
                    .catch(e => { if (live) setErr((e.data && e.data.detail) || e.message); });
                return () => { live = false; };
            }, [subjectId, token]);

            return (
                <div className="panel mb-6">
                    <div className="panel-header font-display text-xl gold">Findability summary</div>
                    <PanelHowTo id="exposure_summary" defaultOpen />
                    {err && <div className="px-6 py-4 text-sm" style={{ color: 'var(--red)' }}>Could not load summary: {err}</div>}
                    {!err && !data && <div className="px-6 py-8 text-muted text-sm">Loading…</div>}
                    {data && (
                        <div className="px-2 py-2">
                            <ExposureSummaryRow title="Adtech location exposure" section={data.location} />
                            <ExposureSummaryRow title="Address exposure" section={data.address} />
                            <ExposureSummaryRow title="Breach exposure" section={data.breach} />
                            <ExposureSummaryRow title="Pipl identity exposure" section={data.pipl} />
                            <ExposureSummaryRow title="OSINT digital footprint" section={data.osint} />
                        </div>
                    )}
                </div>
            );
        }

export { ExposureSummaryPanel };
